/** Which values in a ledger entry are references, and what each one is a reference **to**.
 *
 * An entry's payload is typed identifiers (rule 4), so every id in it names something — and an
 * entry's own page makes the ones that can be followed followable. The classification is a table
 * rather than a pattern on the value, because a UUID alone does not say what it identifies: the same
 * shape is a record, a person, a calculation and a filing, and a link to the wrong kind of page is a
 * page that says "not found" about something that exists.
 *
 * Three destinations and no others:
 *
 * - **record** — `/records/{id}`. Only keys that name a row registered in ADR-0037's supertype. The
 *   record page resolves the kind itself, so a key listed here that turns out not to be visible to
 *   the reader (an engagement narrowed to another period) lands on its one sentence for that, not on
 *   an error.
 * - **person** — `/people/{id}`, **an Admin's page**. The caller decides whether to render the link
 *   at all (`PersonRef` does), because the roster behind it is Admin-only.
 * - **receipt** — the receipt drawer, rule 3's single rendering of a figure. A `calculation_id` is
 *   opened there and nowhere else.
 *
 * Anything else (a hash, an artifact, a route, a decision) renders as text. **An id with no
 * destination is shown, never linked**: a link to nothing is the defect `RecordLink` exists to avoid.
 */

import { isRecordKind } from "./kinds";

export type RefKind = "record" | "person" | "receipt";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Payload keys whose value is the id of a registered record. */
const RECORD_KEYS: ReadonlySet<string> = new Set([
  "record_id",
  "risk_id",
  "activity_record_id",
  "document_id",
  "facility_id",
  "meter_id",
  "initiative_id",
  "about_initiative_id",
]);

/** Keys whose value is a person: anything ending `user_id`, and the named roles in an act. */
const PERSON_KEY = /(^|_)(user_id|verifier_id|attester_id|chooser_id|reviewer_id|submitted_by)$/;

export function isIdentifier(value: unknown): value is string {
  return typeof value === "string" && UUID.test(value);
}

/** What a payload `key` with this `value` refers to, or `null`. */
export function referenceFor(key: string, value: unknown): RefKind | null {
  if (!isIdentifier(value)) return null;
  if (key === "calculation_id") return "receipt";
  if (RECORD_KEYS.has(key)) return "record";
  if (PERSON_KEY.test(key)) return "person";
  return null;
}

/** What an entry's `(object_type, object_id)` refers to: a record when the type is a record kind,
 * and otherwise nothing a page exists for. */
export function subjectReference(objectType: string, objectId: string): RefKind | null {
  if (!isIdentifier(objectId)) return null;
  if (objectType === "calculation") return "receipt";
  if (objectType === "user_account") return "person";
  return isRecordKind(objectType) ? "record" : null;
}
